import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  ExternalLink,
  MessageSquare,
  Shield,
  Upload,
  Wand2,
} from "lucide-react";
import CESSVG from "../../../Assets/svg/CESSVG.svg";

const PAGE_BG = "#ffffff";

const Section = ({ id, title, description, children }) => (
  <section id={id} className="scroll-mt-24">
    <h2 className="font-SFPro font-bold text-xl text-gray-900">{title}</h2>
    {description && (
      <p className="mt-1 text-sm text-gray-600 font-inter">{description}</p>
    )}
    <div className="mt-4">{children}</div>
  </section>
);

export default function DocsHome() {
  const navGroups = useMemo(
    () => [
      {
        title: "Getting Started",
        items: [
          { id: "overview", label: "Overview" },
          { id: "quickstart", label: "Quickstart" },
        ],
      },
      {
        title: "Workflows",
        items: [
          { id: "surveyiq", label: "SurveyIQ" },
          { id: "smartextract", label: "SmartExtract" },
          { id: "upload", label: "Upload & Analyze" },
          { id: "status", label: "Status & Results" },
          { id: "ai-chat", label: "AI Chat" },
        ],
      },
      {
        title: "Admin",
        items: [
          { id: "conversation-tracker", label: "Conversation Tracker" },
        ],
      },
      {
        title: "Help",
        items: [{ id: "tips", label: "Tips & FAQ" }],
      },
    ],
    []
  );

  return (
    <div className="w-full flex flex-col" style={{ background: PAGE_BG, minHeight: "100%" }}>
        {/* Topbar */}
        <div className="fixed top-0 left-0 right-0 z-50 bg-[#0f1f3d] border-b border-white/10">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="h-14 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center shrink-0">
                  <img src={CESSVG} alt="CES logo" className="w-5 h-5" />
                </div>
                <span className="font-SFPro font-bold text-white text-sm">
                  CX2 Documentation
                </span>
              </div>

              <Link
                to="/dashboard"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-300 hover:text-white hover:bg-white/10 transition-colors font-inter focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 focus:ring-offset-[#0f1f3d] shrink-0"
              >
                Back to App
                <ExternalLink className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>

        {/* Body — 2-col layout, pt-14 to clear fixed topbar */}
        <div className="max-w-7xl mx-auto w-full px-6 lg:px-8 pt-20 pb-16 flex-1">
          <div className="flex gap-12">
            {/* Left nav */}
            <aside className="hidden lg:block w-56 shrink-0">
              <div className="sticky top-20 space-y-7">
                {navGroups.map((group) => (
                  <div key={group.title}>
                    <p className="text-[0.65rem] font-bold text-gray-400 uppercase tracking-widest font-inter mb-2 px-2">
                      {group.title}
                    </p>
                    <div className="space-y-0.5">
                      {group.items.map((item) => (
                        <a
                          key={item.id}
                          href={`#${item.id}`}
                          className="block px-2 py-2 text-[0.85rem] font-medium text-gray-600 hover:text-primary-blue hover:bg-blue-50/50 transition-colors font-inter rounded-md"
                        >
                          {item.label}
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </aside>

            {/* Center content */}
            <main className="flex-1 min-w-0">
              {/* Page header */}
              <div className="mb-8 pb-8 border-b border-gray-200/70">
                <h1 className="font-SFPro font-bold text-3xl text-gray-900">
                  CX2 Documentation
                </h1>
                <p className="mt-1 text-xs text-gray-500 font-inter">
                  Last updated March 27, 2026
                </p>
                <p className="mt-4 text-sm text-gray-700 font-inter leading-relaxed max-w-2xl">
                  CX2 is a unified platform for uploading and analyzing files,
                  running AI chat workflows, and monitoring conversations from an
                  admin dashboard.
                </p>
              </div>

              <div className="space-y-12">
                <Section
                  id="overview"
                  title="Overview"
                  description="A quick mental model of how the app is organized."
                >
                  <div className="space-y-3 text-sm text-gray-700 font-inter leading-relaxed">
                    <p>
                      <span className="font-semibold text-gray-900">Dashboard</span>{" "}
                      is where you upload files, see processing metrics, and open results.
                    </p>
                    <p>
                      <span className="font-semibold text-gray-900">AI modules</span>{" "}
                      provide chatbot and workflow tools to accelerate analysis.
                    </p>
                    <p>
                      <span className="font-semibold text-gray-900">Admin</span>{" "}
                      includes Conversation Tracker for monitoring and configuration.
                    </p>
                  </div>

                  <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3">
                    {[
                      { icon: Upload, title: "SurveyIQ & SmartExtract", text: "Upload CSV or PDF files, track processing status, and open result summaries." },
                      { icon: Wand2, title: "AI assistance", text: "Use AI modules to speed up understanding and exploration of your data." },
                      { icon: Shield, title: "Admin oversight", text: "Conversation Tracker helps monitor usage, analytics, and configuration." },
                    ].map(({ icon: Icon, title, text }) => (
                      <div
                        key={title}
                        className="rounded-xl px-5 py-4 border border-blue-100 text-sm font-inter"
                        style={{ background: "linear-gradient(135deg, #ffffff 0%, #eff6ff 100%)" }}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                            <Icon className="w-4 h-4 text-blue-600" />
                          </div>
                          <span className="font-semibold text-gray-900">{title}</span>
                        </div>
                        <p className="text-gray-600">{text}</p>
                      </div>
                    ))}
                  </div>
                </Section>

                <Section
                  id="quickstart"
                  title="Quickstart"
                  description="The fastest path from upload to results."
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white border border-blue-100 rounded-xl p-5">
                      <p className="font-inter font-semibold text-gray-900 text-sm mb-3">
                        Upload a file
                      </p>
                      <ol className="space-y-2 text-sm text-gray-700 font-inter list-decimal pl-5">
                        <li>Go to Dashboard.</li>
                        <li>Select a CSV or PDF and upload.</li>
                        <li>Watch the status table for completion.</li>
                      </ol>
                    </div>
                    <div className="bg-white border border-blue-100 rounded-xl p-5">
                      <p className="font-inter font-semibold text-gray-900 text-sm mb-3">
                        Open results
                      </p>
                      <ol className="space-y-2 text-sm text-gray-700 font-inter list-decimal pl-5">
                        <li>In the status table, click View.</li>
                        <li>Review the summary sections.</li>
                        <li>Use compare tools if available.</li>
                      </ol>
                    </div>
                  </div>
                </Section>

                <Section
                  id="surveyiq"
                  title="SurveyIQ"
                  description="Upload and analyze CSV survey data."
                >
                  <div className="space-y-2 text-sm text-gray-700 font-inter leading-relaxed">
                    <p>
                      <span className="font-semibold text-gray-900">SurveyIQ</span> lets
                      you upload CSV files containing survey responses. Once processed,
                      you get a structured result summary with breakdowns and key insights.
                    </p>
                    <p>
                      Navigate to <span className="font-medium text-gray-900">Dashboard</span>{" "}
                      and use the CSV uploader. The metrics panel above shows total, successful,
                      and failed uploads at a glance.
                    </p>
                  </div>
                </Section>

                <Section
                  id="smartextract"
                  title="SmartExtract"
                  description="Extract structured data from PDF documents."
                >
                  <div className="space-y-2 text-sm text-gray-700 font-inter leading-relaxed">
                    <p>
                      <span className="font-semibold text-gray-900">SmartExtract</span>{" "}
                      processes PDF files and extracts structured content. Navigate to{" "}
                      <span className="font-medium text-gray-900">Dashboard → PDF Analyzer</span>{" "}
                      to upload your documents.
                    </p>
                    <p>
                      After processing, open the result summary to review extracted fields.
                      Use the compare tool to diff two PDFs side by side.
                    </p>
                  </div>
                </Section>

                <Section
                  id="upload"
                  title="Upload & Analyze"
                  description="What happens when you upload CSV/PDF files."
                >
                  <div className="space-y-2 text-sm text-gray-700 font-inter leading-relaxed">
                    <p>
                      Upload components accept files and kick off processing. You can
                      keep working while processing runs in the background.
                    </p>
                    <p>
                      Metrics at the top of the Dashboard show totals and processing outcomes.
                    </p>
                  </div>
                </Section>

                <Section
                  id="status"
                  title="Status & Results"
                  description="Use the status table to monitor and open outputs."
                >
                  <div className="space-y-2 text-sm text-gray-700 font-inter leading-relaxed">
                    <p>
                      The status table lists recent uploads, their processing state, and available actions.
                    </p>
                    <p>
                      Use <span className="font-medium text-gray-900">View</span> to open results.
                      Use <span className="font-medium text-gray-900">Delete</span> to remove an entry.
                    </p>
                  </div>
                </Section>

                <Section
                  id="ai-chat"
                  title="AI Chat"
                  description="Start or continue chats for faster exploration."
                >
                  <div className="space-y-2 text-sm text-gray-700 font-inter leading-relaxed">
                    <p className="flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-primary-blue shrink-0" />
                      Start a new chat from the AI Chat area, or pick a recent one from the sidebar.
                    </p>
                    <p>
                      Rename chats to keep work organized and easier to revisit later.
                    </p>
                  </div>
                </Section>

                <Section
                  id="conversation-tracker"
                  title="Conversation Tracker"
                  description="Monitor usage, analytics, and settings for conversations."
                >
                  <div className="space-y-2 text-sm text-gray-700 font-inter leading-relaxed">
                    <p>
                      Use the tracker dashboard to browse conversations.{" "}
                      <span className="font-medium text-gray-900">Analytics</span> shows
                      trends, and <span className="font-medium text-gray-900">Settings</span>{" "}
                      configures behavior.
                    </p>
                    <p>
                      The top navigation includes a shortcut back to the Admin dashboard.
                    </p>
                  </div>
                </Section>

                <Section
                  id="tips"
                  title="Tips & FAQ"
                  description="Common questions and quick fixes."
                >
                  <div className="space-y-4 text-sm font-inter">
                    <div>
                      <p className="font-semibold text-gray-900">I don't see my upload.</p>
                      <p className="mt-1 text-gray-700 leading-relaxed">
                        Refresh the page and check the status table. If it's still missing,
                        re-upload and confirm file type and size are supported.
                      </p>
                    </div>
                    <div className="border-t border-gray-100 pt-4">
                      <p className="font-semibold text-gray-900">Results look incomplete.</p>
                      <p className="mt-1 text-gray-700 leading-relaxed">
                        Some pipelines may take longer. Check the processing status and
                        revisit the result summary once it shows as processed.
                      </p>
                    </div>
                  </div>
                </Section>
              </div>
            </main>
          </div>
        </div>
    </div>
  );
}
